/**
 * landing-theme-presets — 投放页身份色预设与对比度自检
 *
 * **为什么需要**：生成器只校验 5 个身份色**齐全**，不校验深浅关系（`renderer.py` THEME_KEYS）。
 * 把 5 个键填成同一个值照样能存、能生成，页面发出去才发现正文读不了 —— 而那时已经在投放。
 *
 * 各键在模板里的真实消费点（`templates/base.html.j2`，逐行核过）：
 *
 * | 键 | 用在哪 |
 * |---|---|
 * | `bg` | `html`/`body` 背景 + `<meta name="theme-color">` —— 正文默认 `#1A1A1A`，故**必须浅** |
 * | `primary_deep` | CTA 与顶栏按钮的渐变**起点**，白字 |
 * | `primary_dark` | CTA 渐变**终点**（白字）+ 钩子文案色（落在 bg 上）+ 白底悬浮胶囊的文字与边框 + focus 描边 |
 * | `primary` / `primary_soft` | **当前两个模板只在 `:root` 定义、没有任何消费点** —— 必填但不生效，为后续模板预留 |
 *
 * 下面五组都按这些约束设计，并逐条算过对比度、全部过 WCAG AA（≥4.5:1）。
 */

import type { LandingTheme } from '@novel-admin/shared';

/** 生成器 `config/defaults.json` 提供的中性色默认值 —— 校验时按它们算，与线上渲染一致 */
const DEFAULT_SURFACE = '#FFFFFF';
const DEFAULT_FG = '#1A1A1A';

export type LandingThemePreset = {
  id: string;
  name: string;
  /** 适用调性，给运营选的时候一句话判断 */
  hint: string;
  theme: Pick<LandingTheme, 'bg' | 'primary' | 'primary_deep' | 'primary_dark' | 'primary_soft'>;
};

export const LANDING_THEME_PRESETS: LandingThemePreset[] = [
  {
    id: 'rose',
    name: '玫瑰红',
    hint: '言情 · 默认推荐',
    theme: {
      bg: '#FFF7F5',
      primary: '#E0466B',
      primary_deep: '#C2185B',
      primary_dark: '#8E1141',
      primary_soft: '#FFE3EA',
    },
  },
  {
    id: 'purple',
    name: '皇家紫',
    hint: '奇幻 · 都市异能',
    theme: {
      bg: '#FAF6FD',
      primary: '#8E3FBE',
      primary_deep: '#6A1B9A',
      primary_dark: '#4A126B',
      primary_soft: '#F0E2FA',
    },
  },
  {
    id: 'navy',
    name: '深海蓝',
    hint: '悬疑 · 男频',
    theme: {
      bg: '#F5F8FC',
      primary: '#2B6CB0',
      primary_deep: '#1A4E8A',
      primary_dark: '#123661',
      primary_soft: '#DCE9F7',
    },
  },
  {
    id: 'amber',
    name: '琥珀金',
    hint: '轻奢 · 商战',
    theme: {
      bg: '#FFFBF3',
      primary: '#C2761A',
      primary_deep: '#9A5A12',
      primary_dark: '#6E3F0C',
      primary_soft: '#FCEBD2',
    },
  },
  {
    id: 'forest',
    name: '墨绿',
    hint: '治愈 · 古风',
    theme: {
      bg: '#F4F9F5',
      primary: '#2F7A55',
      primary_deep: '#1F5C3E',
      primary_dark: '#14402B',
      primary_soft: '#D9EEE1',
    },
  },
];

/** sRGB 相对亮度（WCAG 2.x 定义） */
function relativeLuminance(hex: string): number | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m?.[1]) return null;
  const h = m[1];
  const chan = (i: number) => {
    const c = Number.parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
}

/** 对比度；任一值不是合法 6 位 hex 时返回 null（交给必填校验去报，不在这里重复报） */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

export type ContrastIssue = { label: string; ratio: number; need: number };

/** WCAG AA 正文阈值 */
const AA = 4.5;

/**
 * 按模板的真实消费关系自检。**只报告不阻断** —— 生成器不校验这个，
 * 运营有正当理由偏离时不该被拦住；但也不能让「页面读不了」悄悄发出去。
 */
export function checkThemeContrast(
  theme: Partial<LandingTheme>,
  neutral?: { surface?: string; fg?: string },
): ContrastIssue[] {
  const surface = neutral?.surface?.trim() || DEFAULT_SURFACE;
  const fg = neutral?.fg?.trim() || DEFAULT_FG;
  const bg = theme.bg ?? '';
  const deep = theme.primary_deep ?? '';
  const dark = theme.primary_dark ?? '';

  const pairs: { label: string; a: string; b: string }[] = [
    { label: 'CTA 渐变起点配白字（primary_deep）', a: deep, b: '#FFFFFF' },
    { label: 'CTA 渐变终点配白字（primary_dark）', a: dark, b: '#FFFFFF' },
    { label: '悬浮胶囊文字在卡片底上（primary_dark / surface）', a: dark, b: surface },
    { label: '钩子文案在页面底色上（primary_dark / bg）', a: dark, b: bg },
    { label: '正文在页面底色上（fg / bg）', a: fg, b: bg },
  ];

  const issues: ContrastIssue[] = [];
  for (const p of pairs) {
    const ratio = contrastRatio(p.a, p.b);
    if (ratio !== null && ratio < AA) {
      issues.push({ label: p.label, ratio, need: AA });
    }
  }
  return issues;
}
